import { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { CallState, CallInvitation } from '@/types/call';

interface UseCallSystemOptions {
  conversationId: string;
  userId: string;
  otherParticipant?: {
    id: string;
    name: string;
    avatar?: string;
  };
}

export const useCallSystem = ({ conversationId, userId, otherParticipant }: UseCallSystemOptions) => {
  const { toast } = useToast();
  
  // State
  const [callState, setCallState] = useState<CallState>({
    isActive: false,
    isIncoming: false,
    isConnecting: false,
    isVideo: false
  });
  
  const [incomingCall, setIncomingCall] = useState<CallInvitation | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

  // Refs
  const channelRef = useRef<any>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localOfferRef = useRef<RTCSessionDescriptionInit | null>(null);

  // WebRTC configuration
  const rtcConfig = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  };

  // Initialize call system
  useEffect(() => {
    if (!conversationId || !userId) return;

    console.log('🚀 Initializing call system for:', conversationId, 'user:', userId);
    setupCallChannel();

    return () => {
      cleanup();
    };
  }, [conversationId, userId]);

  // Setup Realtime channel for call signaling
  const setupCallChannel = useCallback(() => {
    if (channelRef.current) {
      channelRef.current.unsubscribe();
    }

    const channelName = `simple-calls-${conversationId}`;
    console.log('📡 Setting up call channel:', channelName);

    channelRef.current = supabase
      .channel(channelName, {
        config: {
          broadcast: { self: false, ack: true },
          presence: { key: userId }
        }
      })
      .on('broadcast', { event: 'call-signal' }, handleCallSignal)
      .subscribe((status) => {
        console.log('📡 Call channel status:', status);
        
        if (status === 'SUBSCRIBED') {
          // Send presence to indicate readiness
          channelRef.current?.track({ 
            userId, 
            status: 'ready',
            timestamp: Date.now() 
          });
        }
      });
  }, [conversationId, userId]);

  // Handle incoming call signals
  const handleCallSignal = useCallback(async (payload: any) => {
    const { type, senderId, data } = payload.payload;
    console.log('📞 Call signal received:', type, 'from:', senderId);

    if (senderId === userId) return; // Ignore own signals

    switch (type) {
      case 'call-invitation':
        console.log('📥 Incoming call invitation:', data);
        setIncomingCall({
          senderId,
          senderName: data.senderName,
          senderAvatar: data.senderAvatar,
          isVideo: data.isVideo,
          conversationId,
          timestamp: Date.now(),
          offer: data.offer
        });
        break;

      case 'call-accepted':
        console.log('✅ Call accepted by remote user');
        setCallState(prev => ({ ...prev, isConnecting: true }));
        break;

      case 'call-rejected':
        console.log('❌ Call rejected by remote user');
        handleCallEnd();
        toast({
          title: "Chiamata rifiutata",
          description: "L'utente ha rifiutato la chiamata",
          variant: "destructive"
        });
        break;

      case 'call-ended':
        console.log('📞 Call ended by remote user');
        handleCallEnd();
        break;

      case 'webrtc-offer':
        console.log('🔄 WebRTC offer received');
        await handleRemoteOffer(data.offer);
        break;

      case 'webrtc-answer':
        console.log('🔄 WebRTC answer received');
        await handleRemoteAnswer(data.answer);
        break;

      case 'webrtc-ice':
        console.log('🧊 ICE candidate received');
        await handleRemoteIceCandidate(data.candidate);
        break;
    }
  }, [userId, conversationId]);

  // Start outgoing call
  const startCall = useCallback(async (isVideo: boolean) => {
    if (!otherParticipant) {
      toast({
        title: "Errore",
        description: "Partecipante non trovato",
        variant: "destructive"
      });
      return;
    }

    console.log('📞 Starting', isVideo ? 'video' : 'audio', 'call to:', otherParticipant.name);

    try {
      // Get user media
      const stream = await getUserMedia(isVideo);
      setLocalStream(stream);

      // Create peer connection
      const pc = createPeerConnection();
      peerConnectionRef.current = pc;

      // Add local stream
      stream.getTracks().forEach(track => {
        console.log('➕ Adding local track:', track.kind, track.label);
        pc.addTrack(track, stream);
      });

      // Create offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      localOfferRef.current = offer;

      // Update state
      setCallState({
        isActive: true,
        isIncoming: false,
        isConnecting: true,
        isVideo,
        participantId: otherParticipant.id,
        participantName: otherParticipant.name,
        participantAvatar: otherParticipant.avatar
      });

      // Send invitation
      await sendCallSignal('call-invitation', {
        senderName: 'Tu', // Will be replaced with actual user name
        isVideo,
        offer
      });

      toast({
        title: "Chiamata avviata",
        description: "In attesa di risposta..."
      });

    } catch (error) {
      console.error('❌ Error starting call:', error);
      toast({
        title: "Errore",
        description: "Impossibile avviare la chiamata",
        variant: "destructive"
      });
      handleCallEnd();
    }
  }, [otherParticipant]);

  // Accept incoming call
  const acceptCall = useCallback(async () => {
    if (!incomingCall) return;

    console.log('✅ Accepting call from:', incomingCall.senderName);

    try {
      // Get user media
      const stream = await getUserMedia(incomingCall.isVideo);
      setLocalStream(stream);

      // Create peer connection
      const pc = createPeerConnection();
      peerConnectionRef.current = pc;

      // Add local stream
      stream.getTracks().forEach(track => {
        console.log('➕ Adding local track (accepting call):', track.kind, track.label);
        pc.addTrack(track, stream);
      });

      // Handle the offer
      if (incomingCall.offer) {
        await pc.setRemoteDescription(incomingCall.offer);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        // Send answer
        await sendCallSignal('webrtc-answer', { answer });
      }

      // Update state
      setCallState({
        isActive: true,
        isIncoming: true,
        isConnecting: true,
        isVideo: incomingCall.isVideo,
        participantId: incomingCall.senderId,
        participantName: incomingCall.senderName,
        participantAvatar: incomingCall.senderAvatar
      });

      // Clear incoming call
      setIncomingCall(null);

      // Send acceptance signal
      await sendCallSignal('call-accepted', {});

      toast({
        title: "Chiamata accettata",
        description: "Connessione in corso..."
      });

    } catch (error) {
      console.error('❌ Error accepting call:', error);
      toast({
        title: "Errore",
        description: "Impossibile accettare la chiamata",
        variant: "destructive"
      });
      rejectCall();
    }
  }, [incomingCall]);

  // Reject incoming call
  const rejectCall = useCallback(async () => {
    if (!incomingCall) return;

    console.log('❌ Rejecting call from:', incomingCall.senderName);

    // Send rejection signal
    await sendCallSignal('call-rejected', {});

    // Clear incoming call
    setIncomingCall(null);
  }, [incomingCall]);

  // End call
  const endCall = useCallback(async () => {
    console.log('📞 Ending call');

    // Send signal to remote user
    await sendCallSignal('call-ended', {});

    handleCallEnd();
  }, []);

  // Handle call end (local cleanup)
  const handleCallEnd = useCallback(() => {
    console.log('🧹 Cleaning up call resources');

    // Stop local stream
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }

    // Clear remote stream
    setRemoteStream(null);

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Clear refs
    localOfferRef.current = null;

    // Reset state
    setCallState({
      isActive: false,
      isIncoming: false,
      isConnecting: false,
      isVideo: false
    });

    setIncomingCall(null);
  }, [localStream]);

  // Helper functions
  const getUserMedia = async (isVideo: boolean): Promise<MediaStream> => {
    try {
      console.log('🎤 Requesting user media, video:', isVideo);
      const constraints = {
        video: isVideo ? {
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          frameRate: { ideal: 30, max: 60 },
          facingMode: 'user'
        } : false,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('✅ Media stream obtained:', stream.getTracks().map(t => `${t.kind}: ${t.label}`));
      return stream;
    } catch (error) {
      console.error('❌ Error getting user media:', error);
      throw error;
    }
  };

  const createPeerConnection = (): RTCPeerConnection => {
    const pc = new RTCPeerConnection(rtcConfig);

    pc.ontrack = (event) => {
      console.log('🎥 Remote stream received:', event.streams.length, 'streams');
      const [remoteStream] = event.streams;
      console.log('🎵 Remote tracks:', remoteStream.getTracks().map(t => `${t.kind}: ${t.readyState}`));
      setRemoteStream(remoteStream);
      setCallState(prev => ({ ...prev, isConnecting: false }));
    };

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('🧊 Sending ICE candidate:', event.candidate.type);
        sendCallSignal('webrtc-ice', { candidate: event.candidate });
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('🔗 Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        console.log('✅ WebRTC connection established successfully');
        setCallState(prev => ({ ...prev, isConnecting: false }));
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        console.error('❌ WebRTC connection failed/disconnected');
        handleCallEnd();
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('🧊 ICE connection state:', pc.iceConnectionState);
    };

    pc.onsignalingstatechange = () => {
      console.log('📶 Signaling state:', pc.signalingState);
    };

    return pc;
  };

  const handleRemoteOffer = async (offer: RTCSessionDescriptionInit) => {
    if (!peerConnectionRef.current) return;

    await peerConnectionRef.current.setRemoteDescription(offer);
    const answer = await peerConnectionRef.current.createAnswer();
    await peerConnectionRef.current.setLocalDescription(answer);

    await sendCallSignal('webrtc-answer', { answer });
  };

  const handleRemoteAnswer = async (answer: RTCSessionDescriptionInit) => {
    if (!peerConnectionRef.current) return;

    await peerConnectionRef.current.setRemoteDescription(answer);
    setCallState(prev => ({ ...prev, isConnecting: false }));
  };

  const handleRemoteIceCandidate = async (candidate: RTCIceCandidate) => {
    if (!peerConnectionRef.current) return;

    await peerConnectionRef.current.addIceCandidate(candidate);
  };

  const sendCallSignal = async (type: string, data: any) => {
    if (!channelRef.current) return;

    const payload = {
      type: 'broadcast',
      event: 'call-signal',
      payload: {
        type,
        senderId: userId,
        data,
        timestamp: Date.now()
      }
    };

    console.log('📤 Sending call signal:', type);
    await channelRef.current.send(payload);
  };

  const cleanup = () => {
    console.log('🧹 Cleaning up call system');
    
    if (channelRef.current) {
      channelRef.current.unsubscribe();
      channelRef.current = null;
    }
    
    handleCallEnd();
  };

  return {
    // State
    callState,
    incomingCall,
    localStream,
    remoteStream,
    
    // Actions
    startCall,
    acceptCall,
    rejectCall,
    endCall
  };
};